import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const WarrantyPolicy = () => {
  const exclusions = [
    "Accidental damage, impact, fire, flooding, liquid ingress",
    "Extreme temperatures, humidity, corrosion, or dust exposure",
    "Vehicle electrical faults, battery failure, voltage fluctuations",
    "Power surges, incorrect wiring, polarity reversal",
    "Unauthorized electrical modifications",
    "Damage from jump-starting or improper battery replacement",
    "Installation, removal, or repair by unauthorized personnel",
    "Tampering, opening, or alteration of device casing",
    "Firmware modifications or serial number alterations",
    "Use of non-approved accessories or peripherals"
  ];

  const claimSteps = [
    {
      title: "Notify TrackOn",
      description: "Contact us promptly upon detection of a suspected defect"
    },
    {
      title: "Provide Information",
      description: "Device serial number, vehicle details, original invoice or installation record"
    },
    {
      title: "Allow Inspection",
      description: "Permit TrackOn to conduct remote diagnostics and/or physical inspection"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_099c2006-67c6-4d2e-acf9-1e0fd2c8352f/artifacts/80f6u7mg_Logo%20Final%20Whole%202026.png" 
                alt="TrackOn Logo" 
                className="h-10 object-contain"
              />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-red-600 to-red-800 text-white py-16">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Warranty Policy</h1>
          <p className="text-xl text-red-100">
            Mohammad Alnooryani Vehicles Tracking Systems Services
          </p>
          <p className="mt-2 text-red-200">Effective from date of installation</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-8 py-12">
        {/* Warranty Period */}
        <Card className="mb-8 border-red-200 border-2">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="bg-red-100 rounded-full p-3 flex-shrink-0">
                <Shield className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">24-Month Standard Hardware Warranty</h2>
                <p className="text-gray-700 text-lg">
                  TrackOn provides a comprehensive <strong>Twenty Four (24) months</strong> warranty from the date of installation for all GPS tracking devices.
                </p>
                <div className="mt-4 bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Important:</strong> Warranty start date is determined by TrackOn installation records or original invoice/service documentation. Replacement devices do not reset or extend the original warranty period.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coverage */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What's Covered</h2>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-700">
                      TrackOn warrants that GPS devices will be <strong>free from defects in materials</strong> under normal operating conditions and authorized use.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-700">
                      TrackOn will, at its sole discretion, <strong>repair or replace</strong> defective devices with new or refurbished units of equal or better functionality.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-700">
                      <strong>Important Limitation:</strong> Warranty obligations are strictly limited to the <strong>hardware component</strong> of the solution only.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Conditions */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Warranty Validity Conditions</h2>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">The warranty is valid only if the following conditions are met:</p>
              <div className="space-y-3">
                {[
                  "GPS device was supplied and installed by TrackOn or an authorized technician",
                  "Device is operated in accordance with TrackOn's operational guidelines",
                  "Device remains unaltered, unopened, and untampered with",
                  "All service and subscription dues are fully paid and subscription is active"
                ].map((condition, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="bg-blue-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-gray-700">{condition}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Exclusions */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Exclusions & Limitations</h2>
          <Card className="border-red-200">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4 font-semibold">The warranty does NOT cover:</p>
              <div className="grid md:grid-cols-2 gap-4">
                {exclusions.map((exclusion, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{exclusion}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Claim Procedure */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Claim Warranty</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {claimSteps.map((step, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="bg-red-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl font-bold">{idx + 1}</span>
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Liability */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Liability & Remedies</h2>
          <Card className="bg-yellow-50 border-yellow-300">
            <CardContent className="p-6">
              <div className="space-y-4">
                <p className="text-gray-700">
                  <strong>Limited Liability:</strong> TrackOn's total liability under this warranty is limited to repair or replacement of the defective device only.
                </p>
                <div className="bg-white p-4 rounded-lg border-l-4 border-yellow-600">
                  <p className="text-gray-700 font-semibold mb-2">TrackOn is NOT liable for:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>Loss of business or business opportunity</li>
                    <li>Loss of revenue or profits</li>
                    <li>Vehicle downtime or operational delays</li>
                    <li>Loss of data or information</li>
                    <li>Any indirect, special, or consequential damages</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Out of Warranty */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Out-of-Warranty Support</h2>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">
                For devices outside the warranty period or not covered under warranty terms, TrackOn offers:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-gray-700">Paid repair services</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-gray-700">Device replacement at discounted rates</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-gray-700">Extended support packages</span>
                </div>
              </div>
              <div className="mt-4 bg-gray-100 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Technician Visit Charge:</strong> AED 100 per device (for out-of-warranty devices)
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Legal */}
        <section className="mb-12">
          <Card className="bg-gray-100">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg text-gray-900 mb-3">Governing Law & Jurisdiction</h3>
              <p className="text-gray-700 text-sm mb-3">
                This Warranty Policy is governed by and construed in accordance with the laws of the <strong>United Arab Emirates</strong>. Any disputes arising from or related to this warranty shall be subject to the exclusive jurisdiction of the courts of <strong>Dubai</strong>.
              </p>
              <h3 className="font-bold text-lg text-gray-900 mb-3 mt-6">Policy Amendments</h3>
              <p className="text-gray-700 text-sm">
                TrackOn reserves the right to modify or update this Warranty Policy at any time. The version of this policy that was in effect at the time of device installation will apply to that specific installation.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Contact CTA */}
        <Card className="bg-gradient-to-br from-red-600 to-red-800 text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Need Help with Your Warranty?</h3>
            <p className="mb-6 text-red-100">
              Contact our support team for warranty claims or questions
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:+971508607793">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                  Call: +971 50 860 7793
                </Button>
              </a>
              <a href="mailto:gps@nooryanivts.com">
                <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100">
                  Email: gps@nooryanivts.com
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <p className="text-gray-400 text-sm">
            © 2025 TrackOn - Mohammad Alnooryani Vehicles Tracking Systems Services. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <Link to="/warranty-policy" className="text-red-400 hover:text-red-300">Warranty Policy</Link>
            <Link to="/service-policy" className="text-gray-400 hover:text-white">Service Policy</Link>
            <Link to="/refund-policy" className="text-gray-400 hover:text-white">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WarrantyPolicy;
